/**
 * OSI-806: валидация расчётного счёта (IBAN) на фронте.
 *
 * До этой правки формат счёта на фронте не проверялся вообще — поле не подсвечивалось,
 * сохранение не блокировалось, и пользователь получал общий тост «Указан неверный счет»
 * уже ПОСЛЕ запроса (текст бэка, атрибут [Iban] из IbanNet).
 *
 * Проверяем СТРОГО казахстанские счета — так же, как бэк для страны KZ:
 *   KZ + 2 контрольные цифры + 3 цифры кода банка + 13 символов [A-Z0-9] = ровно 20.
 * Обрати внимание: позиции 5-7 (индексы 4-6) обязаны быть ЦИФРАМИ — это код банка,
 * по которому в кабинете работает автоподстановка БИК.
 *
 * Соответствие «код банка в счёте = выбранный БИК» здесь НЕ проверяется — это отдельно
 * согласовано как «не делаем» (бэк тоже не проверяет, у части банков identifier пустой).
 *
 * Строгость как на бэке (IbanValidator.Strict = true): функция НИЧЕГО не нормализует —
 * не режет пробелы и не поднимает регистр. Нормализация — задача мест ввода:
 * значение приводится к uppercase и очищается от пробелов в onChange
 * (AddAccountButtonFeature/EditAccountButtonFeature/registration ViewModel.onChangeAccount
 * и RowEditor.handleIbanInputChange в шаге СФУ), поэтому в валидатор и на бэк
 * всегда уходит одна и та же уже нормализованная строка.
 */

/**
 * Формат казахстанского IBAN: KZ, 2 контрольные цифры, 3 цифры кода банка,
 * 13 символов [A-Z0-9]. Итого ровно 20 символов.
 */
const KZ_IBAN_REGEXP = /^KZ\d{2}\d{3}[A-Z0-9]{13}$/;

/**
 * Остаток от деления на 97 по стандарту ISO 7064 (MOD 97-10).
 *
 * ВАЖНО: после замены букв на числа (A=10 … Z=35) 20-символьный IBAN превращается
 * в 22–34-значное десятичное число. Оно НЕ влезает в Number (безопасный предел
 * Number.MAX_SAFE_INTEGER ≈ 9·10^15), поэтому считать `Number(str) % 97` нельзя —
 * старшие разряды потеряются и проверка станет случайной.
 * Поэтому идём по строке порциями: держим текущий остаток (0..96) и на каждом шаге
 * дописываем к нему очередную порцию цифр. Максимум получается 4 цифры («96» + «35»),
 * что заведомо безопасно для Number.
 */
const mod97 = (value: string): number => {
  let remainder = 0;

  for (let i = 0; i < value.length; i += 1) {
    const code = value.charCodeAt(i);
    // 'A'(65)…'Z'(90) → 10…35 по ISO 13616; цифры остаются как есть.
    const chunk = code >= 65 && code <= 90 ? String(code - 55) : value[i];
    remainder = Number(`${remainder}${chunk}`) % 97;
  }

  return remainder;
};

/**
 * Проверяет казахстанский IBAN: формат + контрольная сумма mod-97 (ISO 7064).
 *
 * Алгоритм контрольной суммы: первые 4 символа («KZ» + 2 контрольные цифры)
 * переставляются в конец строки, буквы заменяются числами (A=10 … Z=35),
 * полученное число по модулю 97 должно давать 1.
 *
 * @param iban строка счёта БЕЗ пробелов, в верхнем регистре
 * @returns true — счёт валиден
 */
export const checkIban = (iban: string): boolean => {
  if (!iban) return false;
  if (!KZ_IBAN_REGEXP.test(iban)) return false;

  return mod97(`${iban.slice(4)}${iban.slice(0, 4)}`) === 1;
};

/** Длина казахстанского IBAN — используется в местах ввода (maxLength) и в проверках готовности. */
export const KZ_IBAN_LENGTH = 20;
